import React, { useEffect, useRef, useState } from 'react';
import Webcam from "react-webcam";
import { X, Camera, RefreshCw, Upload } from 'lucide-react';
import api from '../../services/api';

const EMPRESAS_SUGERIDAS = ['Ifood', 'Shopee', 'Ali Express', 'Correios', 'Shein', 'Tiktok', '99'];

const getErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        const field = Array.isArray(item?.loc) ? item.loc.slice(1).join(" > ") : "";
        const message = item?.msg || item?.message || "";
        return [field, message].filter(Boolean).join(": ");
      })
      .filter(Boolean)
      .join(" | ");
  }

  if (typeof detail?.message === "string" && detail.message.trim()) {
    return detail.message;
  }

  if (typeof error?.response?.data?.message === "string" && error.response.data.message.trim()) {
    return error.response.data.message;
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }

  return "Erro ao salvar a encomenda.";
};

const EntregaModal = ({ isOpen, onClose, moradores, refreshData }) => {
  const [formData, setFormData] = useState({ morador_id: '', empresa: '', descricao: '' });
  const [searchTerm, setSearchTerm] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [image, setImage] = useState(null);
  const [cameraError, setCameraError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] = useState("environment");
  const [showEmpresaSuggestions, setShowEmpresaSuggestions] = useState(false);
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewFrameRef = useRef(null);

  const isSecureContextForCamera =
    typeof window !== 'undefined' &&
    (window.isSecureContext || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  useEffect(() => {
    return () => {
      if (image?.startsWith?.("blob:")) {
        URL.revokeObjectURL(image);
      }
    };
  }, [image]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setFormData({ morador_id: '', empresa: '', descricao: '' });
    setSearchTerm("");
    setShowResults(false);
    setImage(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setCameraOpen(false);
    setCameraFacingMode("environment");
    setCameraError("");
    setShowEmpresaSuggestions(false);
  }, [isOpen]);

  const empresasFiltradas = EMPRESAS_SUGERIDAS.filter((empresa) =>
    empresa.toLowerCase().includes((formData.empresa || '').toLowerCase())
  );

  const capture = React.useCallback(() => {
    const video = webcamRef.current?.video;
    const frame = previewFrameRef.current;

    if (!video || !frame || !video.videoWidth || !video.videoHeight) {
      setCameraError("Nao foi possivel capturar a imagem da camera.");
      return;
    }

    const frameWidth = frame.clientWidth || video.videoWidth;
    const frameHeight = frame.clientHeight || video.videoHeight;
    const targetAspectRatio = frameWidth / frameHeight;
    const sourceAspectRatio = video.videoWidth / video.videoHeight;

    let sourceWidth = video.videoWidth;
    let sourceHeight = video.videoHeight;
    let sourceX = 0;
    let sourceY = 0;

    if (sourceAspectRatio > targetAspectRatio) {
      sourceWidth = video.videoHeight * targetAspectRatio;
      sourceX = (video.videoWidth - sourceWidth) / 2;
    } else {
      sourceHeight = video.videoWidth / targetAspectRatio;
      sourceY = (video.videoHeight - sourceHeight) / 2;
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(sourceWidth);
    canvas.height = Math.round(sourceHeight);
    const context = canvas.getContext("2d");

    if (!context) {
      setCameraError("Nao foi possivel capturar a imagem da camera.");
      return;
    }

    context.drawImage(
      video,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const imageSrc = canvas.toDataURL("image/jpeg", 0.92);
    setImage(imageSrc);
    setSelectedFile(null);
    setCameraError("");
  }, []);

  const handleCameraError = React.useCallback((error) => {
    const errorName = error?.name || "";

    if (!isSecureContextForCamera) {
      setCameraError("A camera no navegador so funciona em HTTPS ou localhost. Use um acesso seguro ou envie a foto do computador.");
      return;
    }

    if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError") {
      setCameraError("O navegador bloqueou a camera. Libere a permissao da camera no cadeado do site.");
      return;
    }

    if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
      setCameraError("Nenhuma camera foi encontrada neste computador.");
      return;
    }

    if (errorName === "NotReadableError" || errorName === "TrackStartError") {
      setCameraError("A camera esta ocupada por outro programa, como Zoom, Meet ou Teams.");
      return;
    }

    setCameraError("Nao foi possivel abrir a camera neste navegador. Envie uma imagem do computador.");
  }, [isSecureContextForCamera]);

  const handleOpenCamera = React.useCallback(() => {
    setCameraError("");

    if (!isSecureContextForCamera) {
      setCameraOpen(false);
      setCameraError("A camera no navegador so funciona em HTTPS ou localhost. Use um acesso seguro ou envie a foto do computador.");
      return;
    }

    if (image?.startsWith?.("blob:")) {
      URL.revokeObjectURL(image);
    }

    setImage(null);
    setSelectedFile(null);
    setCameraOpen(true);
  }, [image, isSecureContextForCamera]);

  const handleSwitchCamera = React.useCallback(() => {
    setCameraError("");
    setCameraFacingMode((current) => (current === "user" ? "environment" : "user"));
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (image?.startsWith?.("blob:")) {
      URL.revokeObjectURL(image);
    }

    setSelectedFile(file);
    setImage(URL.createObjectURL(file));
    setCameraError("");
    setCameraOpen(false);
  };

  const resetImage = () => {
    if (image?.startsWith?.("blob:")) {
      URL.revokeObjectURL(image);
    }
    setImage(null);
    setSelectedFile(null);
    setCameraOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.morador_id) {
      alert("Selecione um morador!");
      return;
    }
    if (!formData.empresa.trim()) {
      alert("Preencha a empresa!");
      return;
    }
    const data = new FormData();
    data.append('morador_id', formData.morador_id);
    data.append('empresa', formData.empresa);
    data.append('descricao', formData.descricao);
    
    if (selectedFile) {
      data.append('foto', selectedFile, selectedFile.name);
    } else if (image) {
      const res = await fetch(image);
      const blob = await res.blob();
      data.append('foto', blob, 'encomenda.jpg');
    }

    try {
      const response = await api.post('/entregas/', data);
      if (response.data?.notificacao_status === 'enviada') {
        alert("Entrega registrada e morador notificado no WhatsApp!");
      } else if (response.data?.notificacao_status === 'falhou') {
        alert(`Entrega registrada, mas o WhatsApp nao foi enviado: ${response.data?.notificacao_erro || 'verifique a conexao.'}`);
      } else {
        alert("Entrega registrada. Notificacao pendente.");
      }
      refreshData();
      onClose();
    } catch (err) { 
      console.error(err);
      alert(getErrorMessage(err));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-4 py-4 md:px-5">
          <h2 className="text-xl font-bold text-slate-800">Nova Encomenda</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-4 py-4 md:px-5 md:py-5">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
            <div className="space-y-4">
              <div
                ref={previewFrameRef}
                className="relative h-[220px] overflow-hidden rounded-[28px] border border-slate-700/20 bg-slate-950 shadow-sm sm:h-[240px] lg:h-[320px]"
              >
                {!image ? (
                  <>
                    {cameraOpen && !cameraError ? (
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        key={cameraFacingMode}
                        videoConstraints={{ facingMode: cameraFacingMode }}
                        onUserMediaError={handleCameraError}
                        className="h-full w-full object-cover object-center"
                        videoClassName="h-full w-full object-cover object-center"
                      />
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center gap-3 bg-slate-950 px-4 text-center text-sm text-slate-200">
                        <Camera size={32} className="text-slate-400" />
                        <p>
                          {cameraError || "Clique em capturar imagem para abrir a camera ou envie uma foto do computador."}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <img src={image} alt="Encomenda" className="h-full w-full object-cover object-center" />
                )}
                {cameraOpen && !image && !cameraError && (
                  <>
                    <button
                      type="button"
                      onClick={handleSwitchCamera}
                      className="absolute bottom-4 left-4 z-10 inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-3 text-sm font-bold text-slate-800 shadow-lg transition hover:bg-white"
                    >
                      <RefreshCw size={18} />
                      Trocar
                    </button>
                    <button
                      type="button"
                      onClick={capture}
                      className="absolute bottom-4 right-4 z-10 rounded-full bg-slate-900/90 p-3 text-white shadow-lg transition hover:bg-slate-900"
                    >
                      <Camera size={20} />
                    </button>
                  </>
                )}
                {image && (
                  <button
                    type="button"
                    onClick={resetImage}
                    className="absolute bottom-4 right-4 z-10 rounded-full bg-red-500 p-3 text-white shadow-lg transition hover:bg-red-600"
                  >
                    <RefreshCw size={20} />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={handleOpenCamera}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Camera size={16} />
                  Capturar imagem
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Upload size={16} />
                  Enviar foto
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            </div>

            <div className="space-y-4">
            {/* Busca Morador (Mesma lógica das outras abas) */}
            <div className="relative">
            <input 
              type="text" placeholder="Buscar morador..." value={searchTerm}
              className="w-full p-3 bg-gray-50 rounded-xl border"
              onChange={(e) => {setSearchTerm(e.target.value); setShowResults(true)}}
            />
            {showResults && searchTerm && (
               <div className="absolute w-full bg-white border mt-1 rounded-xl shadow-lg max-h-32 overflow-y-auto z-10">
                 {moradores.filter(m => m.nome.toLowerCase().includes(searchTerm.toLowerCase())).map(m => (
                   <div key={m.id} className="p-2 hover:bg-gray-50 cursor-pointer" onClick={() => {setFormData({...formData, morador_id: m.id}); setSearchTerm(m.nome); setShowResults(false)}}>
                     {m.nome} - {m.unidade}
                   </div>
                 ))}
               </div>
            )}
          </div>

          <div className="relative">
            <input
              type="text"
              placeholder="Empresa (Ex: Amazon)"
              className="w-full p-3 bg-gray-50 rounded-xl border"
              value={formData.empresa}
              onFocus={() => setShowEmpresaSuggestions(true)}
              onChange={(e) => {
                setFormData({ ...formData, empresa: e.target.value });
                setShowEmpresaSuggestions(true);
              }}
              required
              autoComplete="off"
            />
            {showEmpresaSuggestions && empresasFiltradas.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-xl border bg-white shadow-lg">
                {empresasFiltradas.map((empresa) => (
                  <button
                    key={empresa}
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, empresa });
                      setShowEmpresaSuggestions(false);
                    }}
                    className="block w-full px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-gray-50"
                  >
                    {empresa}
                  </button>
                ))}
              </div>
            )}
          </div>
          <textarea
            placeholder="Descrição breve..."
            className="w-full p-3 bg-gray-50 rounded-xl border"
            value={formData.descricao}
            onChange={(e) => setFormData({...formData, descricao: e.target.value})}
          />

          <button type="submit" className="w-full bg-slate-900 text-white py-4 rounded-2xl font-bold">Registrar Entrada</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EntregaModal;
